import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Trans, useTranslation } from "react-i18next";

import config from "config/config";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import {
  FACFormDataTextFormat,
  phoneNumberWithUSACodeFormatting,
} from "js/formatting";
import { submitFNOL } from "service/FNOL/submitFNOL";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import FACStepOne from "components/UnauthorizedUser/FileAClaim/FACStepOne";
import FACStepTwo from "components/UnauthorizedUser/FileAClaim/FACStepTwo";
import FACStepThree from "components/UnauthorizedUser/FileAClaim/FACStepThree";
import FACStepFour from "components/UnauthorizedUser/FileAClaim/FACStepFour";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import FileAClaimContactUs from "components/General/FileAClaimContactUs";
import moment from "moment/moment";

export default function UnauthFileAClaim() {
  const uuid = uuidv4();
  const { user } = useSelector((s) => s.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const navigate = useNavigate();
  const {
    trigger,
    getFieldState,
    register: FACStepFiveRegister,
    handleSubmit: FACStepFiveHandleSubmit,
    formState: { errors },
  } = useForm();
  const { t } = useTranslation();
  const [selectedPolicy, setSelectedPolicy] = useState({});

  const [formStep, setFormStep] = useState(1);
  const [FACFormData, setFACFormData] = useState({});
  const [isStepFiveDialogOpen, setIsStepFiveDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [confirmation, setConfirmation] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState();
  const [title, setTitle] = useState();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (formStep === 5) setIsStepFiveDialogOpen(true);
  }, []);

  // if error happens, when user clicks out of the dialog, show last step for resubmit
  useEffect(() => {
    if (!openDialog && formStep === 5) setFormStep(4);
  }, [openDialog]);

  const onError = () => {
    setIsLoading(false);
    setOpenDialog(true);
    setIsStepFiveDialogOpen(false);
    setTitle(t("FNOL-Login-server-error-title"));
    setMessage(t("FNOL-Login-server-error"));
    setFormStep(5);
  };

  async function getFACFormDataString() {
    return new Promise((res, rej) => {
      res(FACFormDataTextFormat(FACFormData));
    });
  }
  function getFACObjectMapper() {
    let phoneNumber = FACFormData?.phoneClaimer
      ? FACFormData?.phoneClaimer
      : FACFormData?.funeralHomePhoneClaimer;
    let amount = !isNaN(FACFormData?.funeralAmountOfAssignment)
      ? Number(FACFormData?.funeralAmountOfAssignment)
      : 0;
    return {
      fnolReceivedFrom: "Customer Portal",
      fnolId: null,
      informantsName: FACFormData?.informantName ?? FACFormData?.funeralName,
      companyName: FACFormData?.companyName ?? "",
      mailsFromToAddress: !FACFormData?.funeralName
        ? `${FACFormData?.individualMailingAddress} ${FACFormData?.individualMailingCityClaimer}, ${FACFormData?.individualMailingStateClaimer} ${FACFormData?.individualMailingZipClaimer}`
        : "",
      mailFormsToName: FACFormData?.informantName ?? "",
      mailFormsToCompanyName: "",
      informantPhoneNumber: phoneNumberWithUSACodeFormatting(phoneNumber),
      informantAddress1: FACFormData?.individualMailingAddress ?? "",
      informantAddress2: FACFormData?.individualMailingAddress2Claimer ?? "",
      informantCity: FACFormData?.individualMailingCityClaimer ?? "",
      informantState: FACFormData?.individualMailingStateClaimer ?? "",
      informantZip: FACFormData?.individualMailingZipClaimer ?? "",
      informantCountry: "",
      informantFaxNumber:
        phoneNumberWithUSACodeFormatting(FACFormData?.faxNumberClaimer) ?? "",
      informantEmail: FACFormData?.inEmailClaimer ?? "",
      relationshipDeceased: FACFormData?.inRelationshipClaimer ?? "",
      formsRequestedBy: "",
      informantCountry: "",
      deceasedFullName: `${FACFormData?.firstName ?? ""} ${
        FACFormData?.lastName ?? ""
      }`,
      deceasedFirstName: FACFormData?.firstName ?? "",
      deceasedLastName: FACFormData?.lastName ?? "",
      deceasedMiddleName: FACFormData?.middleName ?? "",
      deceasedId: FACFormData?.ssn ?? "",
      deceasedDateOfBirth: FACFormData?.dob
        ? moment(FACFormData?.dob).format("MM/DD/YYYY")
        : "",
      deceasedDateOfDeath: FACFormData?.dod
        ? moment(FACFormData?.dod).format("MM/DD/YYYY")
        : "",
      mannerOfDeath: FACFormData?.manner ?? "",
      foreignDeath: FACFormData?.foreign === "no" ? "false" : "true",
      whichCountry: FACFormData?.foreignCountry ?? "",
      fnolType: "D",
      fnolReceivedDate: moment().format("MM/DD/YYYY") ?? "",
      funeralHomeInvolved: FACFormData?.funeralName ? "true" : "false",
      nameFuneralHome: FACFormData?.funeralName ?? "",
      specialInstructions: FACFormData?.notes ?? "",
      funeralPhoneNumber: FACFormData?.funeralHomePhoneClaimer
        ? phoneNumberWithUSACodeFormatting(FACFormData?.funeralHomePhoneClaimer)
        : "",
      funeralEmployeeName: FACFormData?.employeeName ?? "",
      veifiedByGoogle: "",
      funeralAssignment: FACFormData?.funeralHomeAssignment ?? "",
      amountAssignment: amount,
      deathCertificateRequest: "false",
      deathCertificateStatus: "",
      causeOfDeath: FACFormData?.manner ?? "",
      stateOfResidence: "",
      maritalStatus: FACFormData?.marital ?? "",
      nameOfSpouse:
        FACFormData?.marital === "married"
          ? FACFormData?.spouseName
          : FACFormData?.marital === "widowed"
          ? FACFormData?.deceasedSpouse
          : "",
      verifiersName: "",
      callerAgentOnPolicy: "false",
      personalDeliveryBeneficiary: "false",
      personalDeliveryCustomer: "false",
      agentDeliveryPreference: "false",
      policyIssuedCountry: "US",
      region: "US",
      dateOfVerification: "",
      fnolCorrelationId: moment().format("YYYY-MM-DD HH:MM:SS"),
      totalPolicyCount: 1,
    };
  }
  async function onSubmit(data) {
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });

    if (!csrUser?.userId) {
      setIsLoading(true);
      let formattedFACFormData = await getFACFormDataString();
      const fnolRequest = getFACObjectMapper();
      await submitFNOL(
        selectedPolicy,
        formattedFACFormData,
        user.userId,
        fnolRequest
      )
        .then((response) => {
          console.log(response);
          setIsLoading(false);
          setConfirmation(response?.transactionId);
          setIsStepFiveDialogOpen(false);
          setIsConfirmationDialogOpen(true);
          setOpenDialog(false);
        })
        .catch((error) => {
          console.error(error);
          onError();
        });
    } else {
      setOpenDialog(true);
      setIsStepFiveDialogOpen(false);
      setTitle(t("FNOL-Login-server-csr-error"));
      setMessage(t("FNOL-Login-server-csr-error"));
      setFormStep(5);
    }
  }
  useBreadcrumb([
    // unauth customer
    { name: "Home", url: "/" },
    { name: "File a Claim", url: "#" },
  ]);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("FNOL-title")}</h1>
        <p>{t("FNOL-unauthDescription")}</p>
        <hr />
      </div>

      <div className="container ui-component-spacing">
        <div className="grid gap-4 grid-cols-1 lg:gap-6 lg:grid-cols-[66.66667%,33.33333%]">
          <div>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              {/* intro card */}
              <div className="ui-card ui-rte-component p-4 xl:p-6">
                <h2 className="text-xl font-body font-bold">
                  {t("FNOL-cardTitle")}
                </h2>

                <hr className="mt-4 mb-6" />

                <div className="text-sm">
                  <p className="font-semibold">{t("FNOL-cardSub-Title")}</p>

                  <ul className="mt-4 list-disc list-outside ml-8">
                    <li>{t("FNOL-card-List-1")}</li>
                    <li>{t("FNOL-card-List-2")}</li>
                    <li>{t("FNOL-card-List-3")}</li>
                    <li>{t("FNOL-card-List-4")}</li>
                    <li>{t("FNOL-cardList-5")}</li>
                    <li>{t("FNOL-cardList-6")}</li>
                    <li>{t("FNOL-card-List-7")}</li>
                  </ul>
                </div>
              </div>

              <div>
                {/* step 1 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-6 mb-4">
                      <div className="inline-flex justify-center items-center h-8 w-8 text-white bg-ui-blue-dark rounded-full overflow-hidden">
                        1
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>

                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-step-Title1")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 1
                        </div>
                      </div>
                      <p>{t("Unauth-FNOL-stepSubTitle")}</p>
                      <FACStepOne
                        selectedPolicy={selectedPolicy}
                        setSelectedPolicy={setSelectedPolicy}
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                        validate={trigger}
                        getFieldState={getFieldState}
                      />
                    </div>
                  </div>
                </div>

                {/* step 2 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 1
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        2
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle2")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 2
                        </div>
                      </div>

                      <FACStepTwo
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                {/* step 4 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 2
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        3
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle3")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 3
                        </div>
                      </div>

                      <FACStepThree
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                {/* step 4 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 3
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        4
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-transparent overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle4")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 4
                        </div>
                      </div>

                      <FACStepFour
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                        setIsStepFiveDialogOpen={setIsStepFiveDialogOpen}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <FileAClaimContactUs />
        </div>
      </div>

      {/* step 6 dialog form for submit */}
      <Dialog
        open={isStepFiveDialogOpen}
        onClose={() => null}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <EverlakeLoadingOverlay isLoading={isLoading}>
              <Dialog.Title className="font-body text-xl font-bold text-left">
                {t("FNOL-submitDialogTitle")}
              </Dialog.Title>
              <hr />
              <form
                onSubmit={FACStepFiveHandleSubmit(onSubmit)}
                aria-label="File a claim form"
              >
                <div>
                  <label className="mt-2 flex items-center font-normal">
                    <input
                      id={`${uuid}-method-mail`}
                      type="radio"
                      className="mr-1"
                      value="mail"
                      {...FACStepFiveRegister(`method`, {
                        required: t("FNOL-errorMessage-1"),
                      })}
                    />
                    By mail.
                  </label>
                </div>

                <FormInputError
                  id={`${uuid}-method-error`}
                  errorMessage={errors?.method?.message}
                />

                <div className="block text-right mt-6">
                  <button
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    type="button"
                    onClick={() => {
                      setIsStepFiveDialogOpen(false);
                      setFormStep(4);
                    }}
                  >
                    {t("FNOL-submitDialogButton1")}
                  </button>
                  <button
                    id="ID_applyFilterOptions"
                    className="ui-btn-primary ui-btn-min-w mt-4"
                  >
                    {t("FNOL-submitDialogButton2")}
                  </button>
                </div>
              </form>
            </EverlakeLoadingOverlay>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* confirmation modal */}
      <Transition appear show={isConfirmationDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={() => null}
        >
          <DialogBackdrop />
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-2xl p-6 my-32 overflow-hidden text-center align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <Dialog.Title
                  as="h2"
                  className="mx-auto pt-20 pb-8 dialog_label2 font-body text-4xl font-extrabold tracking-tighter text-center"
                >
                  {t("FNOL-confirmationModalTitle")}
                </Dialog.Title>

                <div className="mx-auto lg:w-4/5">
                  <p id="ID_dialogPaymentThankDesc">
                    <strong>{t("FNOL-confirmationModalSubTitle")}</strong>
                  </p>
                  <div className="w-full text-2xl font-bold tracking-tight text-theme-action my-2 text-center xl:text-3xl xl:my-4">
                    {confirmation}
                  </div>
                  <hr />
                  <div className="w-full text-lg font-bold tracking-tight text-black my-2 text-center xl:text-xl xl:my-4">
                    {t("FNOL-confirmationModalStep")}
                  </div>
                  <div className="text-left font-normal tracking-normal px-4">
                    <div className="w-full text-lg font-bold tracking-tight mt-2 text-black my-2 xl:text-xl xl:mb-2 xl:mt-8">
                      {t("FNOL-confirmationModalStepTitle1")}
                    </div>
                    <p className="text-lg font-semibold  max-w-sm">
                      <Trans
                        i18nKey="FNOL-confirmationModalStepDescription1"
                        values={{ email: config.CUSTOMER_SERVICE_EMAIL }}
                        components={{
                          break: <br />,
                        }}
                      />
                    </p>
                    <div className="w-full text-lg font-bold tracking-tight mt-2 text-black my-2 xl:text-xl xl:mb-2 xl:mt-8">
                      {t("FNOL-confirmationModalStepTitle2")}
                    </div>
                    <p className="text-lg font-semibold max-w-md">
                      {t("FNOL-confirmationModalStepDescription2")}
                    </p>
                  </div>
                </div>
                <div className="block text-center mt-12 mb-8 ">
                  <button
                    className="ui-btn-primary min-w-[140px] text-base font-semibold mt-4 py-4 px-8"
                    aria-label="Track claim now"
                    onClick={() => {
                      navigate("/");
                      setIsConfirmationDialogOpen(false);
                    }}
                  >
                    {t("FNOL-confirmationModalButton1")}
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      <GeneralPurposeDialog
        state={openDialog}
        setState={setOpenDialog}
        title={title}
        desc={message}
        secondaryButtonAction={() => {
          setOpenDialog(false);
          setFormStep(4);
        }}
      />
    </>
  );
}
